/**
 * ============================================================
 * KEJJA OS — src/hooks/useCommunity.ts
 * ============================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  communityApi,
  type CommunityCreatePayload,
  type PostCreatePayload, type PostUpdatePayload,
  type GoalCreatePayload, type GoalUpdatePayload,
  type ContributionCreatePayload,
} from '@/lib/communityApi';

const invalidate = (qc: ReturnType<typeof useQueryClient>, homeId: string) => {
  qc.invalidateQueries({ queryKey: ['community', homeId] });
};

// ============================================================
// My communities
// ============================================================

export function useMyCommunities(homeId: string | undefined) {
  return useQuery({
    queryKey: ['community', homeId, 'mine'],
    queryFn: () => communityApi.listMine(homeId!),
    enabled: Boolean(homeId),
  });
}

export function useCreateCommunity(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CommunityCreatePayload) => communityApi.create(homeId!, body),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useJoinByCode(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (joinCode: string) => communityApi.joinByCode(homeId!, joinCode),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

// ============================================================
// One community
// ============================================================

export function useCommunity(homeId: string | undefined, communityId: string | undefined) {
  return useQuery({
    queryKey: ['community', homeId, 'one', communityId],
    queryFn: () => communityApi.get(homeId!, communityId!),
    enabled: Boolean(homeId) && Boolean(communityId),
  });
}

export function useUpdateCommunity(homeId: string | undefined, communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CommunityCreatePayload>) =>
      communityApi.update(homeId!, communityId!, body),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useRegenerateCode(homeId: string | undefined, communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => communityApi.regenerateCode(homeId!, communityId!),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useLeaveCommunity(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (communityId: string) => communityApi.leave(homeId!, communityId),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useCommunityPulse(homeId: string | undefined, communityId: string | undefined) {
  return useQuery({
    queryKey: ['community', homeId, 'pulse', communityId],
    queryFn: () => communityApi.pulse(homeId!, communityId!),
    enabled: Boolean(homeId) && Boolean(communityId),
  });
}

// ============================================================
// Posts
// ============================================================

export function usePosts(homeId: string | undefined, communityId: string | undefined) {
  return useQuery({
    queryKey: ['community', homeId, 'posts', communityId],
    queryFn: () => communityApi.listPosts(homeId!, communityId!),
    enabled: Boolean(homeId) && Boolean(communityId),
  });
}

export function useCreatePost(homeId: string | undefined, communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PostCreatePayload) => communityApi.createPost(homeId!, communityId!, body),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useUpdatePost(homeId: string | undefined, communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ postId, patch }: { postId: string; patch: PostUpdatePayload }) =>
      communityApi.updatePost(homeId!, communityId!, postId, patch),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useDeletePost(homeId: string | undefined, communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (postId: string) => communityApi.deletePost(homeId!, communityId!, postId),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

// ============================================================
// Goals
// ============================================================

export function useGoals(homeId: string | undefined, communityId: string | undefined) {
  return useQuery({
    queryKey: ['community', homeId, 'goals', communityId],
    queryFn: () => communityApi.listGoals(homeId!, communityId!),
    enabled: Boolean(homeId) && Boolean(communityId),
  });
}

export function useCreateGoal(homeId: string | undefined, communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GoalCreatePayload) => communityApi.createGoal(homeId!, communityId!, body),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useUpdateGoal(homeId: string | undefined, communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ goalId, patch }: { goalId: string; patch: GoalUpdatePayload }) =>
      communityApi.updateGoal(homeId!, communityId!, goalId, patch),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useDeleteGoal(homeId: string | undefined, communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (goalId: string) => communityApi.deleteGoal(homeId!, communityId!, goalId),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

// ============================================================
// Contributions
// ============================================================

export function useContributions(
  homeId: string | undefined, communityId: string | undefined, goalId?: string | null,
) {
  return useQuery({
    queryKey: ['community', homeId, 'contributions', communityId, goalId],
    queryFn: () => communityApi.listContributions(homeId!, communityId!, goalId),
    enabled: Boolean(homeId) && Boolean(communityId),
  });
}

export function useCreateContribution(homeId: string | undefined, communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContributionCreatePayload) =>
      communityApi.createContribution(homeId!, communityId!, body),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

export function useDeleteContribution(homeId: string | undefined, communityId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contributionId: string) =>
      communityApi.deleteContribution(homeId!, communityId!, contributionId),
    onSuccess: () => { if (homeId) invalidate(qc, homeId); },
  });
}

// ============================================================
// Linkable transactions
// ============================================================

export function useLinkableTransactions(homeId: string | undefined, daysBack = 30) {
  return useQuery({
    queryKey: ['community', homeId, 'linkable', daysBack],
    queryFn: () => communityApi.listLinkableTransactions(homeId!, daysBack),
    enabled: Boolean(homeId),
  });
}
